import type { Checklist } from "../api/dto/Checklist";
import { usePermissions } from "./usePermissions";

/**
 * Frontend mirror of `App\Policies\ChecklistPolicy` (dijkgraaf-gateway-api) — same two rules, same
 * shape, kept in one place so no page has to re-derive them inline:
 * a global checklist (`companyUuid === null`) is admin-only; a company-scoped one needs
 * `manage_checklists` (resolved by the gateway's CompanyPolicy) for that specific company. This is a UX convenience only — the
 * backend Policy is what actually enforces it, this just avoids offering/doing things that would
 * always be rejected server-side.
 */
export function useChecklistPermissions() {
  const permissions = usePermissions();

  const canCreateChecklist = (companyId: string | null): boolean => {
    if (companyId === null) return permissions.isAdmin;
    return permissions.hasCompanyPermission("manage_checklists", companyId);
  };

  const canManageChecklist = (checklist: Checklist): boolean =>
    canCreateChecklist(checklist.companyUuid);

  const canAccessChecklistSettings = (): boolean =>
    permissions.isAdmin ||
    permissions.companyPermissions.some((company) => company.has("manage_checklists"));

  return {
    loading: permissions.loading,
    isAdmin: permissions.isAdmin,
    canCreateChecklist,
    canManageChecklist,
    canAccessChecklistSettings,
  };
}
